import React, { useLayoutEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Lottie from "lottie-react";
import { sowCurrenciesForJurisdiction } from "../../../utils/sowCurrencies";
import "./KycMobileClientSections.css";
import { MOBILE_TEXT_ENTRY_PROPS } from "./mobileTextEntryProps";

import iconSourceFunds from "../../../images/SoF.json";
import iconSourceWealth from "../../../images/SoW.json";
import iconPaymentRoutes from "../../../images/transfer2-lottie.json";
import iconTurnover from "../../../images/turnover.json";
import iconTurnoverField from "../../../images/refinancing.svg";
import iconNetWorth from "../../../images/piggy-bank.svg";
import iconAnnualIncome from "../../../images/managementX.svg";

const SOW_OPTIONS = [
  { label: "Employment", value: "employment" },
  { label: "Self-employed", value: "self_employed" },
  { label: "Business owner", value: "business_owner" },
  { label: "Sale of company", value: "sale_business" },
  { label: "Real estate sale", value: "sale_real_estate" },
  { label: "Rental income", value: "rental_income" },
  { label: "Investment returns", value: "investment_returns" },
  { label: "Inheritance", value: "inheritance" },
  { label: "Gift", value: "gift" },
  { label: "Legal settlement", value: "legal_settlement" },
  { label: "Divorce settlement", value: "divorce_settlement" },
  { label: "Virtual asset gains", value: "virtual_asset_gains" },
  { label: "Other", value: "other" },
];

const SOF_ALL_OPTIONS = [
  { label: "Employment income", value: "employment_income", sow: ["employment"] },
  { label: "Professional income", value: "professional_income", sow: ["self_employed"] },
  { label: "Business income", value: "business_income", sow: ["business_owner", "self_employed"] },
  { label: "Sale of business proceeds", value: "sale_business_proceeds", sow: ["sale_business", "business_owner"] },
  { label: "Sale of property proceeds", value: "sale_property_proceeds", sow: ["sale_real_estate"] },
  { label: "Rental income", value: "rental_income_sof", sow: ["rental_income"] },
  { label: "Sale of securities", value: "sale_securities", sow: ["investment_returns"] },
  { label: "Dividends distribution", value: "dividends_distribution", sow: ["business_owner", "investment_returns"] },
  { label: "Inheritance proceeds", value: "inheritance_proceeds", sow: ["inheritance"] },
  { label: "Gift proceeds", value: "gift_proceeds", sow: ["gift"] },
  { label: "Legal settlement proceeds", value: "legal_settlement_proceeds", sow: ["legal_settlement"] },
  { label: "Divorce settlement proceeds", value: "divorce_settlement_proceeds", sow: ["divorce_settlement"] },
  { label: "Sale of virtual assets", value: "sale_virtual_assets", sow: ["virtual_asset_gains"] },
  { label: "Loan", value: "loan", sow: null },
  { label: "Other", value: "other", sow: null },
];

const PAYMENT_ROUTES_OPTIONS = [
  { label: "Transfer from own account", value: "transfer_own_account" },
  { label: "Third-party payment", value: "third_party_payment" },
  { label: "Cash deposit", value: "cash_deposit" },
  { label: "Cheque", value: "cheque" },
  { label: "Card payment", value: "card_payment" },
  { label: "Virtual assets transfer", value: "virtual_assets_transfer" },
  { label: "Other", value: "other" },
];

const SOW_OPTION_VALUES = new Set(SOW_OPTIONS.map((opt) => opt.value));
const SOF_OPTION_VALUES = new Set(SOF_ALL_OPTIONS.map((opt) => opt.value));
const PAYMENT_ROUTES_OPTION_VALUES = new Set(PAYMENT_ROUTES_OPTIONS.map((opt) => opt.value));

const NET_WORTH_OPTIONS = [
  "<50k",
  "50k-100k",
  "100k-250k",
  "250k-500k",
  "500k-1m",
  "1m-2m",
  "2m-5m",
  "5m-10m",
  "10m-20m",
  "20m-50m",
  "50m-100m",
  "100m+",
];

const ANNUAL_INCOME_OPTIONS = [
  "<50k",
  "50k-100k",
  "100k-150k",
  "150k-250k",
  "250k-350k",
  "350k-500k",
  "500k-750k",
  "750k-1m",
  "1m-2m",
  "2m-5m",
  "5m-10m",
  "10m+",
];

const ANNUAL_TURNOVER_OPTIONS = [
  "<100k",
  "100k-250k",
  "250k-500k",
  "500k-1m",
  "1m-2m",
  "2m-5m",
  "5m-10m",
  "10m-25m",
  "25m-50m",
  "50m-100m",
  "100m+",
];

const NET_WORTH_LABELS = {
  USD: {
    "<50k": "< 50k",
    "50k-100k": "50k - 100k",
    "100k-250k": "100k - 250k",
    "250k-500k": "250k - 500k",
    "500k-1m": "500k - 1m",
    "1m-2m": "1m - 2m",
    "2m-5m": "2m - 5m",
    "5m-10m": "5m - 10m",
    "10m-20m": "10m - 20m",
    "20m-50m": "20m - 50m",
    "50m-100m": "50m - 100m",
    "100m+": "100m+",
  },
  AED: {
    "<50k": "< 200k",
    "50k-100k": "200k - 400k",
    "100k-250k": "400k - 1m",
    "250k-500k": "1m - 2m",
    "500k-1m": "2m - 4m",
    "1m-2m": "4m - 8m",
    "2m-5m": "8m - 20m",
    "5m-10m": "20m - 40m",
    "10m-20m": "40m - 80m",
    "20m-50m": "80m - 200m",
    "50m-100m": "200m - 400m",
    "100m+": "400m+",
  },
};

const ANNUAL_INCOME_LABELS = {
  USD: {
    "<50k": "< 50k",
    "50k-100k": "50k - 100k",
    "100k-150k": "100k - 150k",
    "150k-250k": "150k - 250k",
    "250k-350k": "250k - 350k",
    "350k-500k": "350k - 500k",
    "500k-750k": "500k - 750k",
    "750k-1m": "750k - 1m",
    "1m-2m": "1m - 2m",
    "2m-5m": "2m - 5m",
    "5m-10m": "5m - 10m",
    "10m+": "10m+",
  },
  AED: {
    "<50k": "< 200k",
    "50k-100k": "200k - 400k",
    "100k-150k": "400k - 550k",
    "150k-250k": "550k - 1m",
    "250k-350k": "1m - 1.3m",
    "350k-500k": "1.3m - 2m",
    "500k-750k": "2m - 2.8m",
    "750k-1m": "2.8m - 4m",
    "1m-2m": "4m - 8m",
    "2m-5m": "8m - 20m",
    "5m-10m": "20m - 40m",
    "10m+": "40m+",
  },
};

const ANNUAL_TURNOVER_LABELS = {
  USD: {
    "<100k": "< 100k",
    "100k-250k": "100k - 250k",
    "250k-500k": "250k - 500k",
    "500k-1m": "500k - 1m",
    "1m-2m": "1m - 2m",
    "2m-5m": "2m - 5m",
    "5m-10m": "5m - 10m",
    "10m-25m": "10m - 25m",
    "25m-50m": "25m - 50m",
    "50m-100m": "50m - 100m",
    "100m+": "100m+",
  },
  AED: {
    "<100k": "< 400k",
    "100k-250k": "400k - 1m",
    "250k-500k": "1m - 2m",
    "500k-1m": "2m - 4m",
    "1m-2m": "4m - 8m",
    "2m-5m": "8m - 20m",
    "5m-10m": "20m - 40m",
    "10m-25m": "40m - 100m",
    "25m-50m": "100m - 200m",
    "50m-100m": "200m - 400m",
    "100m+": "400m+",
  },
};

// CHF uses the same numerical bands as USD, expressed in CHF (no FX conversion).
for (const labels of [NET_WORTH_LABELS, ANNUAL_INCOME_LABELS, ANNUAL_TURNOVER_LABELS]) {
  labels.CHF = labels.USD;
}

function toggleInArray(arr, value) {
  const list = Array.isArray(arr) ? arr : [];
  return list.includes(value)
    ? list.filter((x) => x !== value)
    : [...list, value];
}

function autoResizeTextarea(el) {
  if (!el) return;
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight) || 24;
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  const verticalExtras = paddingTop + paddingBottom + borderTop + borderBottom;
  const minHeight = lineHeight * 3 + verticalExtras;
  const maxHeight = lineHeight * 10 + verticalExtras;

  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  el.style.height = `${nextHeight}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}

function normalizeSelectedOptions(list, validSet) {
  if (!Array.isArray(list)) return [];
  return list.filter((value) => validSet.has(String(value || "").trim()));
}

const DETAIL_TEXTAREA_MIN_HEIGHT = "calc(1.45em * 3 + 1.1rem)";

function InlineDangerMessage({ children, centered = false }) {
  return (
    <div
      className={`kycMobileFieldError ${
        centered ? "kycMobileFieldError--centered" : ""
      }`}
    >
      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{" "}
      {children}
    </div>
  );
}

export default function KycMobileSowSof({
  form,
  orgJurisdiction,
  onSetAt,
  readOnly = false,
  continueAttempted = false,
}) {
  const currencies = sowCurrenciesForJurisdiction(orgJurisdiction);
  // Keep a previously saved currency visible without offering it for new entries.
  const currencyOptions = (savedCurrency) => <>
    {savedCurrency && !currencies.includes(savedCurrency) && <option value={savedCurrency} disabled>{savedCurrency}</option>}
    {currencies.map(currency => <option key={currency} value={currency}>{currency}</option>)}
  </>;
  const sow = form?.sourceOfFundsWealth || {};
  const customerType = String(
    form?.customer?.type ||
      form?.customer?.clientType ||
      form?.customer?.customerType ||
      ""
  )
    .trim()
    .toLowerCase();
  const isEntityClient =
    customerType === "entity" ||
    customerType === "company" ||
    customerType === "corporate";
  const ownership = form?.ownership || {};
  const ownershipProfile = String(ownership?.desktopClientEntityType || "")
    .trim()
    .toLowerCase();
  const ownershipHoldingKind = String(ownership?.desktopClientHoldingKind || "")
    .trim()
    .toLowerCase();
  const ownershipMode = String(
    ownership?.mobilePreliminaryMode ||
      ownership?.assistantPreliminaryMode ||
      ownership?.mobileAssistantDraft?.preliminaryMode ||
      ownership?.assistantTree?.initialAHType ||
      ownership?.preliminaryMode ||
      ""
  )
    .trim()
    .toLowerCase();
  const showsCompanyTurnover =
    isEntityClient &&
    (ownershipProfile === "operating" ||
      (ownershipProfile === "holding" && ownershipHoldingKind === "active") ||
      ownership?.preliminaryNoneOfAbove === true ||
      ownershipMode === "noneofabove" ||
      ownershipMode === "holdingactive");
  const sowOtherRef = useRef(null);
  const sofLoanRef = useRef(null);
  const sofOtherRef = useRef(null);
  const paymentRoutesThirdPartyRef = useRef(null);
  const paymentRoutesOtherRef = useRef(null);
  const sourceOfWealth = normalizeSelectedOptions(
    sow.sourceOfWealth,
    SOW_OPTION_VALUES
  );
  const sourceOfFundsTypes = normalizeSelectedOptions(
    sow.sourceOfFundsTypes,
    SOF_OPTION_VALUES
  );
  const paymentRoutes = normalizeSelectedOptions(
    sow.paymentRoutes,
    PAYMENT_ROUTES_OPTION_VALUES
  );

  const availableSofOptions = SOF_ALL_OPTIONS.filter(
    (opt) => opt.sow === null || opt.sow.some((k) => sourceOfWealth.includes(k))
  );

  const needsSofLoanExplain = sourceOfFundsTypes.includes("loan");
  const needsThirdPartyPaymentExplain = paymentRoutes.includes("third_party_payment");
  const missingSourceOfWealth = continueAttempted && sourceOfWealth.length === 0;
  const missingSourceOfFunds = continueAttempted && sourceOfFundsTypes.length === 0;
  const missingPaymentRoutes = continueAttempted && paymentRoutes.length === 0;
  const missingNetWorth = continueAttempted && !String(sow.netWorthRange || "").trim();
  const missingAnnualIncome = continueAttempted && !String(sow.annualIncomeRange || "").trim();
  const missingAnnualTurnover =
    showsCompanyTurnover &&
    continueAttempted &&
    !String(sow.annualTurnoverRange || "").trim();
  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      autoResizeTextarea(sowOtherRef.current);
      autoResizeTextarea(sofLoanRef.current);
      autoResizeTextarea(sofOtherRef.current);
      autoResizeTextarea(paymentRoutesThirdPartyRef.current);
      autoResizeTextarea(paymentRoutesOtherRef.current);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    sow.sourceOfWealthDetails,
    sow.sourceOfFundsLoanDetails,
    sow.sourceOfFundsOtherDetails,
    sow.paymentRoutesThirdPartyDetails,
    sow.paymentRoutesOtherDetails,
    sow.sourceOfWealth,
    sow.sourceOfFundsTypes,
    sow.paymentRoutes,
  ]);

  function setSow(pathTail, value) {
    onSetAt?.(["sourceOfFundsWealth", ...pathTail], value);
  }

  return (
    <div className="kycMobileSectionStack">
      {showsCompanyTurnover ? (
        <>
          <div className="kycMobileRepeaterCard kycMobileRepeaterCard--softBeige">
            <div className="kycMobileRepeaterHead">
              <div className="kycMobileSectionIconWrap">
                <Lottie
                  animationData={iconTurnover}
                  loop
                  autoplay
                  className="kycMobileSectionLottie"
                />
              </div>
              <div>
                <div className="kycMobileRepeaterTitle">Company turnover</div>
                <div className="kycMobileRepeaterHint">
                  Annual turnover of the company
                </div>
              </div>
            </div>

            <div className="kycMobileSectionStack">
              <div className="kycMobileFieldCard kycMobileCompoundFieldCard">
                <div className="kycMobileCompoundFieldIconCol">
                  <img
                    src={iconTurnoverField}
                    alt=""
                    className="kycMobileCompoundFieldIcon"
                  />
                </div>
                <div className="kycMobileCompoundFieldBody">
                  <label className="kycMobileFieldLabel">Annual turnover</label>
                  <div className="kycMobileCompoundFieldInputs">
                    <select
                      className="form-select kycMobileCompactSelect kycMobileCompactSelect--currency"
                      value={sow.annualTurnoverCurrency || "USD"}
                      onChange={(e) =>
                        setSow(["annualTurnoverCurrency"], e.target.value)
                      }
                      disabled={readOnly}
                    >
                      {currencyOptions(sow.annualTurnoverCurrency)}
                    </select>
                    <select
                      className={`form-select kycMobileCompactSelect ${
                        sow.annualTurnoverRange ? "is-selected" : ""
                      } kycMobileCompactSelect--range kycMobileCompactSelect--turnoverRange`}
                      value={sow.annualTurnoverRange || ""}
                      onChange={(e) =>
                        setSow(["annualTurnoverRange"], e.target.value)
                      }
                      disabled={readOnly}
                    >
                      <option value="">Select</option>
                      {ANNUAL_TURNOVER_OPTIONS.map((value) => (
                        <option key={value} value={value}>
                          {ANNUAL_TURNOVER_LABELS[
                            sow.annualTurnoverCurrency || "USD"
                          ]?.[value] || ANNUAL_TURNOVER_LABELS.USD[value]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {missingAnnualTurnover ? (
                    <InlineDangerMessage>
                      Select an annual turnover range
                    </InlineDangerMessage>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <div className="kycMobileRepeaterCard kycMobileRepeaterCard--softBeige">
        <div className="kycMobileRepeaterHead">
          <div className="kycMobileSectionIconWrap">
            <Lottie
              animationData={iconSourceWealth}
              loop
              autoplay
              className="kycMobileSectionLottie"
            />
          </div>
          <div>
            <div className="kycMobileRepeaterTitle">Overall financial profile</div>
            <div className="kycMobileRepeaterHint">
              Overview of how wealth was built and general financial capacity
            </div>
          </div>
        </div>

        <div className="kycMobileSectionStack">
          <div className="kycMobileFieldCard">
            <label className="kycMobileFieldLabel">Source of wealth</label>
            <div className="kycMobileFieldHint">
              Select one or multiple sources
            </div>
            <div className="kycMobileChipGrid">
              {SOW_OPTIONS.map((opt) => {
                const active = sourceOfWealth.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`kycMobileChip ${active ? "is-active" : ""}`}
                    disabled={readOnly}
                    onClick={() =>
                      setSow(
                        ["sourceOfWealth"],
                        toggleInArray(sourceOfWealth, opt.value)
                      )
                    }
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {missingSourceOfWealth ? (
              <InlineDangerMessage centered>
                Select at least one source of wealth
              </InlineDangerMessage>
            ) : null}
          </div>

          <AnimatePresence>
            {sourceOfWealth.includes("other") ? (
              <motion.div
                key="sow-other"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                  opacity: { duration: 0.22, delay: 0.1 },
                }}
                style={{ overflow: "hidden" }}
              >
                <div className="kycMobileFieldCard">
                  <label className="kycMobileFieldLabel">Other source of wealth</label>
                  <textarea
                    {...MOBILE_TEXT_ENTRY_PROPS}
                    ref={sowOtherRef}
                    className="form-control kycMobileAutoGrowTextarea"
                    rows={3}
                    style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                    value={sow.sourceOfWealthDetails || ""}
                    onChange={(e) => {
                      autoResizeTextarea(e.currentTarget);
                      setSow(["sourceOfWealthDetails"], e.target.value);
                    }}
                    onInput={(e) => autoResizeTextarea(e.currentTarget)}
                    disabled={readOnly}
                  />
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>

          <div className="kycMobileFieldCard kycMobileCompoundFieldCard">
            <div className="kycMobileCompoundFieldIconCol">
              <img src={iconNetWorth} alt="" className="kycMobileCompoundFieldIcon" />
            </div>
            <div className="kycMobileCompoundFieldBody">
              <label className="kycMobileFieldLabel">Net worth</label>
              <div className="kycMobileCompoundFieldInputs">
                <select
                  className="form-select kycMobileCompactSelect kycMobileCompactSelect--currency"
                  value={sow.netWorthCurrency || "USD"}
                  onChange={(e) => setSow(["netWorthCurrency"], e.target.value)}
                  disabled={readOnly}
                >
                  {currencyOptions(sow.netWorthCurrency)}
                </select>
                <select
                  className={`form-select kycMobileCompactSelect ${
                    sow.netWorthRange ? "is-selected" : ""
                  } kycMobileCompactSelect--range`}
                  value={sow.netWorthRange || ""}
                  onChange={(e) => setSow(["netWorthRange"], e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">Select</option>
                  {NET_WORTH_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {NET_WORTH_LABELS[sow.netWorthCurrency || "USD"]?.[value] ||
                        NET_WORTH_LABELS.USD[value]}
                    </option>
                  ))}
                </select>
              </div>
              {missingNetWorth ? (
                <InlineDangerMessage>
                  Select a net worth range
                </InlineDangerMessage>
              ) : null}
            </div>
          </div>

          <div className="kycMobileFieldCard kycMobileCompoundFieldCard">
            <div className="kycMobileCompoundFieldIconCol">
              <img
                src={iconAnnualIncome}
                alt=""
                className="kycMobileCompoundFieldIcon"
              />
            </div>
            <div className="kycMobileCompoundFieldBody">
              <label className="kycMobileFieldLabel">Annual income</label>
              <div className="kycMobileCompoundFieldInputs">
                <select
                  className="form-select kycMobileCompactSelect kycMobileCompactSelect--currency"
                  value={sow.annualIncomeCurrency || "USD"}
                  onChange={(e) =>
                    setSow(["annualIncomeCurrency"], e.target.value)
                  }
                  disabled={readOnly}
                >
                  {currencyOptions(sow.annualIncomeCurrency)}
                </select>
                <select
                  className={`form-select kycMobileCompactSelect ${
                    sow.annualIncomeRange ? "is-selected" : ""
                  } kycMobileCompactSelect--range`}
                  value={sow.annualIncomeRange || ""}
                  onChange={(e) => setSow(["annualIncomeRange"], e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">Select</option>
                  {ANNUAL_INCOME_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {ANNUAL_INCOME_LABELS[
                        sow.annualIncomeCurrency || "USD"
                      ]?.[value] || ANNUAL_INCOME_LABELS.USD[value]}
                    </option>
                  ))}
                </select>
              </div>
              {missingAnnualIncome ? (
                <InlineDangerMessage>
                  Select an annual income range
                </InlineDangerMessage>
              ) : null}
            </div>
          </div>

        </div>
      </div>

      <div className="kycMobileRepeaterCard kycMobileRepeaterCard--softBeige">
        <div className="kycMobileRepeaterHead">
          <div className="kycMobileSectionIconWrap">
            <Lottie
              animationData={iconSourceFunds}
              loop
              autoplay
              className="kycMobileSectionLottie"
            />
          </div>
          <div>
            <div className="kycMobileRepeaterTitle">Funding Source</div>
            <div className="kycMobileRepeaterHint">
              Immediate origin of money used in this relationship
            </div>
          </div>
        </div>

        <div className="kycMobileSectionStack">
          <div className="kycMobileFieldCard">
            <label className="kycMobileFieldLabel">Source of funds</label>
            <div className="kycMobileFieldHint">
              Select one or multiple sources
            </div>
            <div className="kycMobileChipGrid">
              {availableSofOptions.map((opt) => {
                const active = sourceOfFundsTypes.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`kycMobileChip ${active ? "is-active" : ""}`}
                    disabled={readOnly}
                    onClick={() =>
                      setSow(
                        ["sourceOfFundsTypes"],
                        toggleInArray(sourceOfFundsTypes, opt.value)
                      )
                    }
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {missingSourceOfFunds ? (
              <InlineDangerMessage centered>
                Select at least one source of funds
              </InlineDangerMessage>
            ) : null}
          </div>

          <AnimatePresence>
            {needsSofLoanExplain ? (
              <motion.div
                key="sof-loan"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                  opacity: { duration: 0.22, delay: 0.1 },
                }}
                style={{ overflow: "hidden" }}
              >
                <div className="kycMobileFieldCard">
                  <label className="kycMobileFieldLabel">Loan details</label>
                  <textarea
                    {...MOBILE_TEXT_ENTRY_PROPS}
                    ref={sofLoanRef}
                    className="form-control kycMobileAutoGrowTextarea"
                    rows={3}
                    style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                    value={sow.sourceOfFundsLoanDetails || ""}
                    onChange={(e) => {
                      autoResizeTextarea(e.currentTarget);
                      setSow(["sourceOfFundsLoanDetails"], e.target.value);
                    }}
                    onInput={(e) => autoResizeTextarea(e.currentTarget)}
                    disabled={readOnly}
                    placeholder="Lender name, purpose and repayment terms…"
                  />
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>

          <AnimatePresence>
            {sourceOfFundsTypes.includes("other") ? (
              <motion.div
                key="sof-other"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                  opacity: { duration: 0.22, delay: 0.1 },
                }}
                style={{ overflow: "hidden" }}
              >
                <div className="kycMobileFieldCard">
                  <label className="kycMobileFieldLabel">Other source of funds</label>
                  <textarea
                    {...MOBILE_TEXT_ENTRY_PROPS}
                    ref={sofOtherRef}
                    className="form-control kycMobileAutoGrowTextarea"
                    rows={3}
                    style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                    value={sow.sourceOfFundsOtherDetails || ""}
                    onChange={(e) => {
                      autoResizeTextarea(e.currentTarget);
                      setSow(["sourceOfFundsOtherDetails"], e.target.value);
                    }}
                    onInput={(e) => autoResizeTextarea(e.currentTarget)}
                    disabled={readOnly}
                  />
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>

      {/* ── Card 3: Payment routes ── */}
      <div className="kycMobileRepeaterCard kycMobileRepeaterCard--softBeige">
        <div className="kycMobileRepeaterHead">
          <div className="kycMobileSectionIconWrap">
            <Lottie
              animationData={iconPaymentRoutes}
              loop
              autoplay
              className="kycMobileSectionLottie"
            />
          </div>
          <div>
            <div className="kycMobileRepeaterTitle">Payment Routes</div>
            <div className="kycMobileRepeaterHint">
              How the funds are transferred into this relationship
            </div>
          </div>
        </div>

        <div className="kycMobileSectionStack">
          <div className="kycMobileFieldCard">
            <label className="kycMobileFieldLabel">Payment routes</label>
            <div className="kycMobileFieldHint">
              Select one or multiple routes
            </div>
            <div className="kycMobileChipGrid">
              {PAYMENT_ROUTES_OPTIONS.map((opt) => {
                const active = paymentRoutes.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`kycMobileChip ${active ? "is-active" : ""}`}
                    disabled={readOnly}
                    onClick={() =>
                      setSow(
                        ["paymentRoutes"],
                        toggleInArray(paymentRoutes, opt.value)
                      )
                    }
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {missingPaymentRoutes ? (
              <InlineDangerMessage centered>
                Select at least one payment route
              </InlineDangerMessage>
            ) : null}
          </div>

          <AnimatePresence>
            {needsThirdPartyPaymentExplain ? (
              <motion.div
                key="payment-routes-third-party"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                  opacity: { duration: 0.22, delay: 0.1 },
                }}
                style={{ overflow: "hidden" }}
              >
                <div className="kycMobileFieldCard">
                  <label className="kycMobileFieldLabel">Third-party payment details</label>
                  <textarea
                    {...MOBILE_TEXT_ENTRY_PROPS}
                    ref={paymentRoutesThirdPartyRef}
                    className="form-control kycMobileAutoGrowTextarea"
                    rows={3}
                    style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                    value={sow.paymentRoutesThirdPartyDetails || ""}
                    onChange={(e) => {
                      autoResizeTextarea(e.currentTarget);
                      setSow(["paymentRoutesThirdPartyDetails"], e.target.value);
                    }}
                    onInput={(e) => autoResizeTextarea(e.currentTarget)}
                    disabled={readOnly}
                    placeholder="Name and relationship of the third party…"
                  />
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>

          <AnimatePresence>
            {paymentRoutes.includes("other") ? (
              <motion.div
                key="payment-routes-other"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                  opacity: { duration: 0.22, delay: 0.1 },
                }}
                style={{ overflow: "hidden" }}
              >
                <div className="kycMobileFieldCard">
                  <label className="kycMobileFieldLabel">Other payment route</label>
                  <textarea
                    {...MOBILE_TEXT_ENTRY_PROPS}
                    ref={paymentRoutesOtherRef}
                    className="form-control kycMobileAutoGrowTextarea"
                    rows={3}
                    style={{ minHeight: DETAIL_TEXTAREA_MIN_HEIGHT }}
                    value={sow.paymentRoutesOtherDetails || ""}
                    onChange={(e) => {
                      autoResizeTextarea(e.currentTarget);
                      setSow(["paymentRoutesOtherDetails"], e.target.value);
                    }}
                    onInput={(e) => autoResizeTextarea(e.currentTarget)}
                    disabled={readOnly}
                  />
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
